// Lib
import React from 'react';
import { connect, useSelector } from 'react-redux';

// Selectors
import { activePopupSelector } from '../../../../../components/popupPanel/popupOpenSelector';

// Utils
import {
    getSelectedElementColorStatusClass,
    getFirstSelectedSecondaryColor,
    getSelectedElementType,
    getSupportColorBar,
} from './colorToolUtils';

// Components
import ToolbarPopupTool from '../../ToolbarPopupTool';
import ColorToolIcon from './ColorToolIcon';
import ColorPopup from './popup/desktop/DesktopColorPopup';

// Selectors
import { colorToolSelector, ColorToolSelectorReturnType } from './colorToolSelector';

// Constants
import { BACKGROUND_COLORS } from '../../../../../../common/colors/colorConstants';
import { PopupIds } from '../../../../../components/popupPanel/popupConstants';

// Types
import { ToolbarItemConfig } from '../../../toolbarTypes';
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

// Styles
import './DesktopColorTool.scss';

type DesktopColorToolProps = ColorToolSelectorReturnType & {
    selectedElements: Immutable.List<ImMNElement>;
    editorKey: string;
    item: ToolbarItemConfig;
    // This is used by the DocumentModalSidebar to ensure the correct button is targeted
    buttonSelector?: string;
};

const ColorToolPopupIcon = (props: Omit<DesktopColorToolProps, 'editorKey' | 'item'> & { isPopupOpen: boolean }) => {
    const {
        selectedElements,
        selectedColor,
        selectedBackgroundColor,
        selectedBackgroundColorName,
        supportBackgroundColor,
        isPopupOpen,
    } = props;
    const selectedType = getSelectedElementType(selectedElements);
    const selectedStatus = getSelectedElementColorStatusClass(selectedElements);
    const supportColorBar = getSupportColorBar(selectedElements);
    const secondaryColor = getFirstSelectedSecondaryColor(selectedElements);

    const backgroundColor = BACKGROUND_COLORS[selectedBackgroundColorName] ?? { css: selectedBackgroundColor };

    return (
        <ColorToolIcon
            key={`${selectedColor}${selectedBackgroundColor}`}
            supportColorBar={supportColorBar}
            supportBackgroundColor={supportBackgroundColor}
            selectedType={selectedType}
            selectedStatus={selectedStatus}
            color={selectedColor}
            backgroundColor={backgroundColor}
            isActive={isPopupOpen}
            secondaryColor={secondaryColor}
        />
    );
};

const DesktopColorTool = (props: DesktopColorToolProps) => {
    const { item, buttonSelector = '.ColorTool .ColorToolIcon' } = props;

    // Calculate if popup is open using the same logic as ToolbarPopupTool
    const activePopup = useSelector(activePopupSelector);
    const popupId = item?.uid || PopupIds.COLOR;
    const isPopupOpen = activePopup.has(popupId);

    return (
        <ToolbarPopupTool
            {...props}
            toolName="color"
            buttonSelector={buttonSelector}
            popupId={item?.uid || PopupIds.COLOR}
            IconComponent={(iconProps: Omit<DesktopColorToolProps, 'editorKey' | 'item'>) => (
                <ColorToolPopupIcon {...iconProps} isPopupOpen={isPopupOpen} />
            )}
            PopupComponent={ColorPopup}
        />
    );
};

const enhance = connect(colorToolSelector);

export default enhance(DesktopColorTool);
